// Words for the messenger's surface: when a message was sent, how big a file is, the mention
// spans in a body. Pure, so they are tested without the page (format.test.ts).

import { dayMonth } from '../day.js';

const pad = (n: number): string => String(n).padStart(2, '0');

/** "10:41" today, "Tue 10:41" this week, "12 Sep" this year, "12 Sep 2025" before. */
export function whenWords(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay = d.toDateString() === now.toDateString();
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (sameDay) return time;
  const days = (now.getTime() - d.getTime()) / 86_400_000;
  if (days < 6 && days > 0) return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${time}`;
  const day = dayMonth(d);
  return d.getFullYear() === now.getFullYear() ? day : `${day} ${d.getFullYear()}`;
}

/** The day a run of messages belongs to: "Today", "Yesterday", else the date. */
export function dayWords(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (d.toDateString() === now.toDateString()) return 'Today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) });
}

export function sameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

export function sizeWords(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type Span = { kind: 'text'; text: string } | { kind: 'mention'; text: string };

/** The body split into text and @mention spans, so a mention is drawn as one; a name is one or two words after the @. */
export function mentionSpans(body: string, names: readonly string[]): Span[] {
  if (names.length === 0 || !body.includes('@')) return [{ kind: 'text', text: body }];
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const out: Span[] = [];
  let i = 0;
  const re = /(^|[^A-Za-z0-9._-])@/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    const at = m.index + m[1]!.length;
    const rest = body.slice(at + 1);
    const hit = sorted.find((n) => rest.toLowerCase().startsWith(n.toLowerCase()) && !/[A-Za-z0-9]/.test(rest.charAt(n.length)))
      ?? sorted.map((n) => n.split(' ')[0]!).find((n) => rest.toLowerCase().startsWith(n.toLowerCase()) && !/[A-Za-z0-9]/.test(rest.charAt(n.length)))
      ?? (['everyone', 'all', 'here'].find((n) => rest.toLowerCase().startsWith(n) && !/[A-Za-z0-9]/.test(rest.charAt(n.length))));
    if (!hit) continue;
    if (at > i) out.push({ kind: 'text', text: body.slice(i, at) });
    out.push({ kind: 'mention', text: `@${rest.slice(0, hit.length)}` });
    i = at + 1 + hit.length;
    re.lastIndex = i;
  }
  if (i < body.length) out.push({ kind: 'text', text: body.slice(i) });
  return out.length ? out : [{ kind: 'text', text: body }];
}

/** The two-letter mark on a face: first letters of the first two words. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('') || '?';
}
